// src/components/shared/UI.jsx
// Shared UI primitives — single source of truth for all reusable components

import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { Pencil, Plus, X as XIcon, ChevronUp, ChevronDown } from 'lucide-react';

// ── Persisted state hook ──────────────────────────────
// Behaves like useState but reads/writes localStorage under `key`.
// State survives page refreshes and across sessions.
export function usePersistedState(key, defaultValue) {
  const [state, setState] = useState(() => {
    try {
      const stored = localStorage.getItem(key);
      return stored !== null ? JSON.parse(stored) : defaultValue;
    } catch { return defaultValue; }
  });

  const setPersistedState = useCallback((value) => {
    setState((prev) => {
      const next = typeof value === 'function' ? value(prev) : value;
      try { localStorage.setItem(key, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [key]); // eslint-disable-line

  return [state, setPersistedState];
}

// ── Document Heading 1 ────────────────────────────────
// Uppercase label + full-width gradient underline that fades right.
// color: CSS colour string. actions: optional JSX (edit button etc.)
export const DocH1 = ({ children, color, actions, style = {} }) => (
  <div className="doc-h1" style={{ '--doc-color': color, ...style }}>
    <div className="doc-h1-row">
      <span className="doc-h1-label">{children}</span>
      {actions}
    </div>
    <div className="doc-h1-rule" />
  </div>
);

// ── Icon Action Buttons ────────────────────────────────
// Small square buttons for inline edit / add / delete actions.
// No children → 24×24 icon only. Pass children for icon + label.
export const EditBtn = ({ onClick, title = 'Edit', size = 12, disabled, children, style = {} }) => (
  <button type="button" className="ib ib-edit" onClick={onClick} title={title} disabled={disabled} style={style}>
    <Pencil size={size} />{children}
  </button>
);
export const AddBtn = ({ onClick, title = 'Add', size = 12, disabled, children, style = {} }) => (
  <button type="button" className="ib ib-add" onClick={onClick} title={title} disabled={disabled} style={style}>
    <Plus size={size} />{children}
  </button>
);
export const DelBtn = ({ onClick, title = 'Remove', size = 12, disabled, children, style = {}, className = '' }) => (
  <button type="button" className={`ib ib-del ${className}`.trim()} onClick={onClick} title={title} disabled={disabled} style={style}>
    <XIcon size={size} />{children}
  </button>
);

// ── Loading Screen ─────────────────────────────────────
export const LoadingScreen = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading…</p>
    </div>
  </div>
);

// ── Button ────────────────────────────────────────────
// variant: 'primary' | 'secondary' | 'success' | 'danger' | 'danger-outline' | 'outline' | 'ghost'
// size: 'sm' | 'md' | 'lg'
export const Button = ({ children, variant = 'primary', size = 'md', onClick, type = 'button', disabled, style = {}, ...rest }) => {
  const sizeClass = size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '';
  const variantClass = `btn-${variant}`;
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`btn ${variantClass} ${sizeClass}`.trim()}
      style={style}
      {...rest}
    >
      {children}
    </button>
  );
};

// ── Form primitives ───────────────────────────────────
export const FormGroup = ({ children, style = {} }) => (
  <div className="form-group" style={style}>{children}</div>
);

export const FormLabel = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="form-label">{children}</label>
);

export const FormInput = ({ id, value, onChange, placeholder, type = 'text', maxLength, disabled, style = {} }) => (
  <input
    id={id}
    type={type}
    value={value ?? ''}
    onChange={onChange}
    placeholder={placeholder}
    maxLength={maxLength}
    disabled={disabled}
    className="form-input"
    style={style}
  />
);

export const FormTextarea = ({ id, value, onChange, placeholder, rows = 4, maxLength, disabled, style = {} }) => (
  <textarea
    id={id}
    value={value ?? ''}
    onChange={onChange}
    placeholder={placeholder}
    rows={rows}
    maxLength={maxLength}
    disabled={disabled}
    className="form-input"
    style={{ resize: 'vertical', ...style }}
  />
);

export const FormSelect = ({ id, value, onChange, children, disabled, style = {} }) => (
  <select
    id={id}
    value={value ?? ''}
    onChange={onChange}
    disabled={disabled}
    className="form-input"
    style={{ background: 'white', ...style }}
  >
    {children}
  </select>
);

// ── Field Error ───────────────────────────────────────
export const FieldError = ({ children }) => (
  <div style={{ fontSize: '11px', color: '#CC0000', marginTop: '3px', fontWeight: 600, fontFamily: "'Exo 2', sans-serif" }}>
    ↑ {children}
  </div>
);

// ── Modal ─────────────────────────────────────────────
// Standard modal using index.css classes
export const Modal = ({ open, onClose, title, children, footer, width }) => {
  if (!open) return null;
  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box" style={width ? { maxWidth: width } : {}}>
        {title && (
          <div className="modal-header">
            <span>{title}</span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}>✕</button>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

// ── Confirm Dialog ────────────────────────────────────
const ConfirmDialog = ({ title, message, onConfirm, onCancel, confirmLabel, cancelLabel }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
  }}>
    <div style={{ background: 'white', borderRadius: '6px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', fontFamily: "'Exo 2', sans-serif" }}>
      <div style={{ background: '#0e4e95', padding: '12px 16px', borderRadius: '6px 6px 0 0' }}>
        <span style={{ color: 'white', fontSize: '14px', fontWeight: 700 }}>{title}</span>
      </div>
      <div style={{ padding: '20px 16px' }}>
        <p style={{ margin: 0, fontSize: '13px', color: '#1a1a1a', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{message}</p>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', gap: '8px', justifyContent: 'flex-end', borderTop: '1px solid #d0daea' }}>
        <button onClick={onCancel} className="btn btn-secondary btn-sm">{cancelLabel}</button>
        <button onClick={onConfirm} className="btn btn-danger btn-sm">{confirmLabel}</button>
      </div>
    </div>
  </div>
);

// Usage:
//   const { confirm, dialog } = useConfirm();
//   const ok = await confirm('Delete Reference', 'Are you sure?');
//   if (!ok) return;
//   // ... then include {dialog} somewhere in your JSX
export const useConfirm = () => {
  const [state, setState] = useState({
    open: false, title: '', message: '',
    confirmLabel: 'Yes', cancelLabel: 'No', resolve: null,
  });

  const confirm = useCallback((title, message, options = {}) => new Promise(resolve => {
    setState({
      open: true, title, message,
      confirmLabel: options.confirmLabel || 'Yes',
      cancelLabel:  options.cancelLabel  || 'No',
      resolve,
    });
  }), []);

  const handleConfirm = useCallback(() => {
    setState(s => { s.resolve?.(true);  return { ...s, open: false }; });
  }, []);

  const handleCancel = useCallback(() => {
    setState(s => { s.resolve?.(false); return { ...s, open: false }; });
  }, []);

  const dialog = state.open ? ReactDOM.createPortal(
    <ConfirmDialog
      title={state.title}
      message={state.message}
      confirmLabel={state.confirmLabel}
      cancelLabel={state.cancelLabel}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />,
    document.body
  ) : null;

  return { confirm, dialog };
};

// ── Section Box ───────────────────────────────────────
// ── CollapsibleSection ────────────────────────────────
// Card with a clickable header bar that toggles the body open/closed.
// Persists state across sessions via usePersistedState.
// Props: title, storageKey (required for persistence), defaultOpen (default true),
//        action (optional node rendered right of chevron, click does not toggle),
//        children (body).
export const CollapsibleSection = ({
  title, storageKey, defaultOpen = true, action, children,
  headerBg = '#4A6FA5', headerColor = '#E8EDF2',
  wrapperStyle = {}, bodyStyle = {},
}) => {
  const [open, setOpen] = usePersistedState(storageKey, defaultOpen);
  return (
    <div style={{
      background: 'white', border: '1px solid #d0daea', borderRadius: 6,
      marginBottom: 16, overflow: 'hidden', ...wrapperStyle,
    }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          background: headerBg, color: headerColor,
          padding: '10px 14px', border: 'none', cursor: 'pointer',
          fontFamily: 'inherit', fontSize: 14, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left',
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </span>
        {action && (
          <span onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center' }}>
            {action}
          </span>
        )}
        {open
          ? <ChevronUp size={14} style={{ flexShrink: 0, opacity: 0.85 }} />
          : <ChevronDown size={14} style={{ flexShrink: 0, opacity: 0.85 }} />}
      </button>
      {open && (
        <div style={{ padding: '16px 20px', ...bodyStyle }}>
          {children}
        </div>
      )}
    </div>
  );
};

export const SectionBox = ({ title, children, action }) => (
  <div className="section-box">
    {title && (
      <div className="section-box-header">
        <span>{title}</span>
        {action && <span>{action}</span>}
      </div>
    )}
    <div className="section-box-body">{children}</div>
  </div>
);

// ── Page Header ───────────────────────────────────────
export const PageHeader = ({ title, subtitle, action }) => (
  <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div>
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </div>
    {action && <div>{action}</div>}
  </div>
);

// ── Tag / Badge ───────────────────────────────────────
// variant: '' | 'primary' | 'secondary' | 'green' | 'red' | 'amber' | 'ghost'
export const Tag = ({ children, variant = '' }) => (
  <span className={`tag${variant ? ` tag-${variant}` : ''}`}>{children}</span>
);

// ── Skill Tag ─────────────────────────────────────────
export const SkillTag = ({ name, matched, missing }) => {
  const cls = matched ? 'tag tag-green' : missing ? 'tag tag-red' : 'tag';
  return <span className={cls}>{name}</span>;
};

// ── Status Badge ──────────────────────────────────────
export const StatusBadge = ({ status }) => {
  const config = {
    ACTIVE:      { label: 'Active',      variant: 'green'   },
    DRAFT:       { label: 'Draft',       variant: 'ghost'   },
    CLOSED:      { label: 'Closed',      variant: 'red'     },
    PAUSED:      { label: 'Paused',      variant: 'amber'   },
    PENDING:     { label: 'Pending',     variant: 'ghost'   },
    SHORTLISTED: { label: 'Shortlisted', variant: 'green'   },
    REJECTED:    { label: 'Rejected',    variant: 'red'     },
    REVIEWED:    { label: 'Reviewed',    variant: ''        },
  };
  const { label, variant } = config[status] || { label: status, variant: 'ghost' };
  return <Tag variant={variant}>{label}</Tag>;
};

// ── Score Badge ───────────────────────────────────────
export const ScoreBadge = ({ score }) => {
  const cls = score >= 80 ? 'score-high' : score >= 60 ? 'score-mid' : score >= 40 ? 'score-low' : 'score-poor';
  return (
    <div className={`score-badge ${cls}`}>
      <span className="score-badge-value">{score}</span>
      <span className="score-badge-label">/ 100</span>
    </div>
  );
};

// ── Score Bar ─────────────────────────────────────────
export const ScoreBar = ({ label, score, weight }) => (
  <div className="score-bar-row">
    <span className="score-bar-label">{label}</span>
    <div className="score-bar-track">
      <div className="score-bar-fill" style={{ width: `${score}%` }} />
    </div>
    <span className="score-bar-value">{score}</span>
    {weight !== undefined && <span style={{ fontSize: 11, color: 'var(--text-muted)', width: 60, textAlign: 'right' }}>{Math.round(weight * 100)}% wt</span>}
  </div>
);

// ── Stat Card ─────────────────────────────────────────
export const StatCard = ({ label, value, icon: Icon }) => (
  <div className="stat-box">
    <div className="stat-box-value">{value}</div>
    <div className="stat-box-label">{label}</div>
  </div>
);

// ── Empty State ───────────────────────────────────────
export const EmptyState = ({ icon: Icon, title, description, action }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 16px', textAlign: 'center' }}>
    {Icon && (
      <div style={{ width: 56, height: 56, borderRadius: 8, background: 'var(--tag-bg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
        <Icon size={24} style={{ color: 'var(--text-muted)' }} />
      </div>
    )}
    <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: '0 0 6px' }}>{title}</h3>
    {description && <p style={{ fontSize: 13, color: 'var(--text-muted)', maxWidth: 320, margin: '0 0 20px' }}>{description}</p>}
    {action}
  </div>
);

// ── Spinner ───────────────────────────────────────────
export const Spinner = ({ size = 20 }) => (
  <div style={{ width: size, height: size, border: `2px solid var(--border)`, borderTopColor: 'var(--primary)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
);

// ── Skill Extract Modal ────────────────────────────────
// Usage:
//   <SkillExtractModal
//     open={!!extractModal}
//     onClose={() => setExtractModal(null)}
//     sourceLabel="Bachelor Degree — Computer Science"
//     suggestedSkills={extractModal?.skills || []}
//     existingSkillNames={extractModal?.profileSkillNames || []}
//     onAdd={async (skillNames) => { ... }}
//   />
export const SkillExtractModal = ({ open, onClose, sourceLabel, suggestedSkills = [], alreadyLinkedSkillNames = [], onAdd }) => {
  const [selected, setSelected] = useState(new Set());
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (open) {
      const linkedLower = new Set((alreadyLinkedSkillNames || []).map(s => s.toLowerCase()));
      const newSkills = (suggestedSkills || []).filter(s => !linkedLower.has(s.toLowerCase()));
      setSelected(new Set(newSkills));
    }
  }, [open]); // intentionally omits suggestedSkills/alreadyLinkedSkillNames — selection resets only when modal opens

  if (!open) return null;

  const linkedLower = new Set((alreadyLinkedSkillNames || []).map(s => s.toLowerCase()));
  const isLinked = (skill) => linkedLower.has(skill.toLowerCase());

  const toggleSkill = (skill) => {
    if (isLinked(skill) || adding) return;
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(skill)) next.delete(skill); else next.add(skill);
      return next;
    });
  };

  const selectedCount = selected.size;

  const handleAdd = async () => {
    if (selected.size === 0 || adding) return;
    setAdding(true);
    try { await onAdd([...selected]); }
    finally { setAdding(false); }
  };

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={e => { if (e.target === e.currentTarget && !adding) onClose(); }}>
      <div className="modal-box" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <span>Extract Skills</span>
          <button onClick={onClose} disabled={adding} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}>✕</button>
        </div>
        <div className="modal-body">
          {sourceLabel && (
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 12px', fontStyle: 'italic' }}>{sourceLabel}</p>
          )}
          {suggestedSkills.length === 0 ? (
            <p style={{ color: '#666', fontSize: 13, margin: 0 }}>
              No skills could be extracted. Try adding more detail to the roles and tasks, then extract again.
            </p>
          ) : (
            <>
              <p style={{ fontSize: 12, color: '#666', margin: '0 0 14px' }}>
                Click to select or deselect skills. Skills already sourced from this entry are shown greyed out.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {suggestedSkills.map(skill => {
                  const linked = isLinked(skill);
                  const sel = selected.has(skill);
                  return (
                    <button
                      key={skill}
                      onClick={() => toggleSkill(skill)}
                      disabled={linked || adding}
                      style={{
                        padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        fontFamily: "'Exo 2', sans-serif", cursor: linked ? 'default' : 'pointer',
                        border: linked ? '1px solid #d0daea' : sel ? '1px solid #0e4e95' : '1px solid #d0daea',
                        background: linked ? '#f4f4f4' : sel ? '#0e4e95' : 'white',
                        color: linked ? '#aaa' : sel ? 'white' : '#444',
                        transition: 'all 0.12s',
                      }}
                    >
                      {linked ? `✓ ${skill}` : skill}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} disabled={adding} className="btn btn-secondary btn-sm">Cancel</button>
          {suggestedSkills.length > 0 && (
            <button
              onClick={handleAdd}
              disabled={selectedCount === 0 || adding}
              className="btn btn-primary btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {adding
                ? <><Spinner size={12} /> Adding…</>
                : `Add ${selectedCount} Skill${selectedCount !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
